import { computed, ref, unref } from 'vue';

/** El mismo tope que valida la API (ExportSelection::MAX_IDS). */
export const EXPORT_SELECTION_MAX_IDS = 500;

/**
 * Filas marcadas en un listado para elegir qué exportar (ADSREC-0029).
 *
 * - Marcar filas una a una guarda sus IDs, también al pasar de página o al
 *   cambiar los filtros, para ir juntando lo que se quiere bajar.
 * - «Seleccionar todo» marca todo lo filtrado, también lo de otras páginas.
 *   Desmarcar una fila la deja fuera. Al cambiar los filtros se suelta
 *   (`releaseAll`), porque ya no es el mismo «todo».
 *
 * `exportParams` es lo que se le pasa a la API: `ids` con las filas marcadas
 * o `exclude_ids` con las desmarcadas de «todo», como lista separada por
 * comas. Sin selección, nada: se exporta lo filtrado, como hasta ahora.
 *
 * @param {import('vue').Ref<number>|number} total filas del listado filtrado
 */
export function useRowSelection(total) {
  const allFiltered = ref(false);
  // Filas marcadas; con «seleccionar todo», las desmarcadas.
  const ids = ref(new Set());

  const totalRows = () => Number(unref(total)) || 0;

  const count = computed(() => (allFiltered.value ? Math.max(totalRows() - ids.value.size, 0) : ids.value.size));
  const allSelected = computed(() => allFiltered.value && ids.value.size === 0);
  const partlySelected = computed(() => count.value > 0 && !allSelected.value);
  /** Solo filas marcadas a mano: la exportación no necesita rango de fechas. */
  const picksRows = computed(() => !allFiltered.value && ids.value.size > 0);
  const tooMany = computed(() => ids.value.size > EXPORT_SELECTION_MAX_IDS);

  const exportParams = computed(() => {
    if (!ids.value.size) return {};

    return { [allFiltered.value ? 'exclude_ids' : 'ids']: [...ids.value].join(',') };
  });

  function clear() {
    allFiltered.value = false;
    ids.value = new Set();
  }

  function isSelected(item) {
    return allFiltered.value !== ids.value.has(item?.id);
  }

  function toggle(item) {
    if (item?.id === undefined || item?.id === null) return;

    const next = new Set(ids.value);
    if (next.has(item.id)) {
      next.delete(item.id);
    } else {
      next.add(item.id);
    }
    ids.value = next;

    // Desmarcadas todas las filas de «todo»: no queda nada marcado.
    if (allFiltered.value && count.value === 0) clear();
  }

  function toggleAll() {
    if (allSelected.value) {
      clear();

      return;
    }
    allFiltered.value = true;
    ids.value = new Set();
  }

  function releaseAll() {
    if (allFiltered.value) clear();
  }

  return {
    allFiltered: computed(() => allFiltered.value),
    count, allSelected, partlySelected, picksRows, tooMany, exportParams, isSelected, toggle, toggleAll, releaseAll, clear,
  };
}
